/**
 * Theme mode — the runtime half of docs/010-theming-and-tokens.md.
 *
 * The tokens for both appearances already exist in
 * `src/styles/themes/default.css`: the bare `:root` block is light, and the
 * same keys are redefined under `@media (prefers-color-scheme: dark)
 * :root:not([data-theme="light"])` and `:root[data-theme="dark"]`. Nothing in
 * this module knows a colour — it only decides which of those blocks wins, by
 * setting (or clearing) `data-theme` on `<html>`:
 *
 *   "system"  →  attribute removed        → the media query decides
 *   "light"   →  data-theme="light"       → the media block is excluded
 *   "dark"    →  data-theme="dark"        → the forced-dark block applies
 *
 * React-free on purpose: the pre-paint inline script in each app's `index.html`
 * has to do the same thing before any bundle loads, and the two must agree.
 * If you change `THEME_STORAGE_KEY` or the attribute contract, change that
 * script too.
 */
/** Every mode, in the order a picker should present them. */
export const THEME_MODES = ["light", "dark", "system"];
/**
 * Shared with the pre-paint script in `index.html`. Namespaced because a SPA
 * shares an origin with every other console under the same distribution
 * (docs/013) — an unprefixed `theme` key would collide.
 */
export const THEME_STORAGE_KEY = "instanexus.theme";
/**
 * Light until told otherwise, whatever the OS asks for. Following the OS is a
 * choice a person makes in the picker, stored as `system`.
 */
export const DEFAULT_THEME_MODE = "light";
const DARK_MEDIA_QUERY = "(prefers-color-scheme: dark)";
const THEME_ATTRIBUTE = "data-theme";
export function isThemeMode(value) {
    return (value === "light" || value === "dark" || value === "system");
}
/** `true` in a browser with a DOM. Guards SSR and the pre-hydration render. */
function hasDocument() {
    return typeof document !== "undefined";
}
/**
 * The stored preference, or the default.
 *
 * Storage access throws outright in Safari's private mode and under a
 * restrictive cookie policy, and a colour preference is never worth taking the
 * app down for — an unreadable store degrades to the default, which is the same
 * thing a first-time visitor gets.
 */
export function readStoredThemeMode() {
    if (typeof localStorage === "undefined")
        return DEFAULT_THEME_MODE;
    try {
        const stored = localStorage.getItem(THEME_STORAGE_KEY);
        return isThemeMode(stored) ? stored : DEFAULT_THEME_MODE;
    }
    catch {
        return DEFAULT_THEME_MODE;
    }
}
/** Persists the preference. Silently a no-op when storage is unavailable. */
export function writeStoredThemeMode(mode) {
    if (typeof localStorage === "undefined")
        return;
    try {
        localStorage.setItem(THEME_STORAGE_KEY, mode);
    }
    catch {
        /* Storage unavailable — the preference lives for this session only. */
    }
}
/** What the OS currently asks for. Defaults to light where it cannot be read. */
export function getSystemTheme() {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
        return "light";
    }
    return window.matchMedia(DARK_MEDIA_QUERY).matches ? "dark" : "light";
}
/** Collapses `system` down to what will actually render. */
export function resolveTheme(mode) {
    return mode === "system" ? getSystemTheme() : mode;
}
/**
 * Writes the mode to `<html>` and returns what it resolved to.
 *
 * `color-scheme` is set alongside `data-theme` so the browser's own surfaces —
 * scrollbars, form controls, the canvas behind an overscroll — follow the app
 * instead of staying on the OS setting. `light dark` for `system` hands that
 * same decision back to the OS.
 */
export function applyThemeMode(mode) {
    const resolved = resolveTheme(mode);
    if (!hasDocument())
        return resolved;
    const root = document.documentElement;
    if (mode === "system") {
        root.removeAttribute(THEME_ATTRIBUTE);
        root.style.colorScheme = "light dark";
    }
    else {
        root.setAttribute(THEME_ATTRIBUTE, mode);
        root.style.colorScheme = mode;
    }
    return resolved;
}
/**
 * Calls `listener` whenever the OS preference flips. Returns the unsubscribe.
 *
 * Only worth subscribing while the mode is `system`; a pinned mode ignores the
 * OS by definition.
 */
export function subscribeToSystemTheme(listener) {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
        return () => undefined;
    }
    const query = window.matchMedia(DARK_MEDIA_QUERY);
    const onChange = (event) => {
        listener(event.matches ? "dark" : "light");
    };
    query.addEventListener("change", onChange);
    return () => {
        query.removeEventListener("change", onChange);
    };
}
//# sourceMappingURL=theme-mode.js.map